import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {Slot, TicketConfiguration, TicketConfigurationCollection} from '../../../../Models';
import TicketDescription from '../TicketDescription/TicketDescription.vue';
import TicketPrice from '../TicketPrice/TicketPrice.vue';
import IntoCart from '../../../Icons/IntoCart.vue';
import Check from '../../../Icons/Check.vue';
import Arrow from '../../../Icons/Arrow.vue';
import Modal from '../../../Modal/Modal.vue';
import {ProductQuantityUtility} from '../../../../Utilities';
import {TicketConfigurationMixin} from '../../../../Mixins/TicketConfigurationMixin';

@Component({
  components: {
    IntoCart,
    Check,
    Arrow,
    Modal,
    TicketPrice,
    TicketDescription,
  },
})
export default class TicketConfigurationCollectionItem extends TicketConfigurationMixin {
  @Prop({type: TicketConfigurationCollection, required: false, default: false}) ticketConfigurationCollection: TicketConfigurationCollection | undefined;
  @Prop({type: Array, required: true}) selectedSlots: Array<number> | undefined;
  @Prop({type: Boolean, default: () => false}) loading: boolean | undefined;

  @Watch('ticketConfigurationCollection.quantity')
  onQuantityChanged(newVal: number, oldVal: number): void {
    if (newVal && this.ticketConfigurationCollection) {
      const isProdFamily = this.ticketConfigurationCollection.ticketConfigurations[0].subTicketConfigurations.length > 0;
      const product = this.ticketConfigurationCollection.ticketConfigurations[0].product;
      if (product.maxOrderQuantity && newVal > product.maxOrderQuantity) {
        this.ticketConfigurationCollection.quantity = isProdFamily ? 1 : product.maxOrderQuantity;
      }
      if (newVal < 0) {
        this.ticketConfigurationCollection.quantity = 0;
      }

      if (newVal > 1 && isProdFamily) {
        this.ticketConfigurationCollection.quantity = 1;
      }
    }
  }

  private selectSlot(ticketConfiguration: TicketConfiguration): void {
    this.$emit('selectSlot', ticketConfiguration);
  }

  private onSlotSelectionModalOpen(ticketConfigurationCollection: TicketConfigurationCollection) {
    this.$emit('onSlotSelectionModalOpen', ticketConfigurationCollection);
  }

  private changeSelection($event: Event, index: number, slotKey: number): void {
    this.$emit('changeSelection', $event, index, slotKey);
  }

  get smallestQuotaSlot(): Slot | null {
    if (this.slotsProp && this.firstTicketConfiguration) {
      return this.firstTicketConfiguration!.product.slots
        .map(sId => this.slotsProp!.find(s => s.id === sId))
        .filter(s => s !== undefined)
        .reduce(
          function (prev: Slot | null | undefined, curr: Slot | null | undefined) {
            return prev !== undefined && curr !== undefined && prev !== null && curr !== null && prev.quota < curr.quota ? prev : curr;
          },
          null,
        ) || null;
    }
    return null;
  }

  get reachedQuotaLimit(): boolean {
    if (this.firstTicketConfiguration && this.smallestQuotaSlot) {
      return this.firstTicketConfiguration.quantity >= this.smallestQuotaSlot.quota;
    }
    return false;
  }

  get allSlotsSelected(): boolean {
    if (this.selectedSlots && this.ticketConfigurationCollection) {
      if (this.selectedSlots.length !== this.ticketConfigurationCollection.quantity) {
        return false;
      }
      for (let i = 0; i < this.selectedSlots.length; i++) {
        if (typeof this.selectedSlots[i] !== 'number') {
          return false;
        }
      }
      return true;
    }
    throw new Error('Error selecting slots!');
  }

  private addPreselectionToCart(ticketConfigurationCollection: TicketConfigurationCollection): void {
    this.$emit('addPreselectionToCart', ticketConfigurationCollection);
    // @ts-ignore
    if (this.$refs.slotSelectionModal && typeof this.$refs.slotSelectionModal.closeModal === 'function') {
      // @ts-ignore
      this.$refs.slotSelectionModal.closeModal();
    }
  }

  get isSoldOut() {
    if (this.slotsProp) {
      return this.firstTicketConfiguration.product.slots.find(sId => {
        const slot = this.slotsProp!.find(s => s.id === sId);
        return slot && slot.quota === 0;
      }) !== undefined;
    }
    return false;
  }

  get firstTicketConfiguration(): TicketConfiguration {
    if (this.ticketConfigurationCollection && this.ticketConfigurationCollection.ticketConfigurations) {
      return this.ticketConfigurationCollection.ticketConfigurations[0];
    }
    throw new Error('Ticket configuration collection has no valid TicketConfigurations!');
  }

  get disableIncrease(): boolean {
    if (this.ticketConfigurationCollection) {
      return this.ticketConfigurationCollection.quantity >= this.ticketConfigurationCollection.maxQuantity
        || (this.ticketConfigurationCollection.hasProductFamilies && this.ticketConfigurationCollection.quantity === 1)
        || !this.productsPurchasable
        || this.reachedMaxPositionQuantity
        || this.reachedMaxOrderQuantity
        || this.isSoldOut
        || this.reachedQuotaLimit
        || !this.additionalProductPurchasable;
    }
    return true;
  }

  get productsPurchasable() {
    if (this.ticketConfigurationCollection) {
      // all have the same product
      const product = this.ticketConfigurationCollection.ticketConfigurations[0].product;
      const purchasableFrom = new Date(product.bookingPosStart);
      const purchasableUntil = new Date(product.bookingPosEnd);
      const now = new Date();
      return purchasableFrom <= now && purchasableUntil >= now;
    }
    return true;
  }

  /**
   * This one is only purchasable if there is at least one item in cart with a value of > 0€ of the same event
   */
  get additionalProductPurchasable(): boolean {
    let purchasable = false;
    if (this.ticketConfigurationCollection && this.ticketConfigurationCollection.ticketConfigurations[0].isAdditionalProduct) {
      if (this.cart === null) {
        return false;
      }

      const eventId = this.ticketConfigurationCollection.ticketConfigurations[0].event.id;

      this.cart.order.orderItems.forEach((oi) => {
        const product = this.findProduct(oi.productId);
        if (product && product.price > 0 && product.eventId === eventId) {
          purchasable = true;
        }
      });
    }
    return purchasable;
  }

  get disableDecrease(): boolean {
    if (this.ticketConfigurationCollection) {
      return this.ticketConfigurationCollection.quantity === 0;
    }
    return true;
  }

  get reachedMaxPositionQuantity(): boolean {
    if (this.ticketConfigurationCollection) {
      // all have the same product
      const product = this.ticketConfigurationCollection.ticketConfigurations[0].product;
      return (new ProductQuantityUtility(this.currentOrder))
        .reachedMaxPositionQuantity(product, this.ticketConfigurationCollection.quantity);
    }
    return false;
  }

  get reachedMaxOrderQuantity(): boolean {
    if (this.ticketConfigurationCollection) {
      // all have the same product
      const product = this.ticketConfigurationCollection.ticketConfigurations[0].product;
      // TODO: check whether there can be multiple order positions of the same product in an order
      return (new ProductQuantityUtility(this.currentOrder))
        .reachedMaxOrderQuantity(product, this.ticketConfigurationCollection.quantity);
    }
    return false;
  }

  get reachedMinPositionQuantity() {
    if (this.ticketConfigurationCollection) {
      // all have the same product
      const product = this.ticketConfigurationCollection.ticketConfigurations[0].product;
      return (new ProductQuantityUtility(this.currentOrder))
        .reachedMinPositionQuantity(product, this.ticketConfigurationCollection.quantity);
    }
    return false;
  }

  get currentTotalOfProduct() {
    let currentTotalOfProduct = 0;
    if (this.ticketConfigurationCollection) {
      // all have the same product
      const product = this.ticketConfigurationCollection.ticketConfigurations[0].product;
      currentTotalOfProduct = (new ProductQuantityUtility(this.currentOrder))
        .currentTotalOfProduct(product);
    }
    return currentTotalOfProduct;
  }
}
